import {Collapse, css, useTheme} from '@mui/material';
import {
  DlMuiChip,
  DlMuiComponentBox,
  enumContainer,
  GetStylesFunction,
  IconHeaderWithSearch,
  LightIconButton,
  MaximizeButton,
  NewMinimizedMaximized,
  useLocalStorage,
} from '@wallaby-ui/core';
import * as React from 'react';
import {useState} from 'react';
import {useStore} from 'react-redux';
import {DocumentColors, MerieSchema, useDocumentInfo, useFormatters} from 'src/js/common';
import {applicationSlice, APPLICATION_REDUCER_STATE_ID} from 'src/js/containers/application';
import {useAppDispatch, useAppSelector} from 'src/js/store_hooks';

import {DlMuiDocumentConcepts} from './DlMuiDocumentConcepts';
import {MedicationPlaceholderDocument} from './MedicationPlaceholderDocument';
import {RenderedDocument} from './RenderedDocument';
import {RawDocumentData} from '../hooks';

type Props = {
  documentData: RawDocumentData;
  /** Document-version id from the URL — threaded down because the MERIE
      DocumentViewDto does not carry it but the placeholder-medications
      endpoint needs it. */
  documentVersionId?: string | number;
  storageIdPrefix: string;
  maximized: boolean;
  setMinimizedMaximized: (newValue: NewMinimizedMaximized) => void;
};

type MerieEventTypeEnum = MerieSchema.components['schemas']['MerieEventTypeEnum'];
function useMerieDocumentColors(eventTypeEnum: enumContainer.EnumDefinition[]): (eventType: MerieEventTypeEnum) => DocumentColors {
  type DocumentColorsPartial = Omit<DocumentColors, 'text'> & {text?: string};
  const COLOR_LIST: DocumentColorsPartial[] = [
    {background: '#91EEFF', highlight: '#11B9CC'}, // 0
    {background: '#FFD7D9', highlight: '#FF8389'}, // 1
    {background: '#E8DAFF', highlight: '#BE95FF'}, // 2
    {background: '#D0E2FF', highlight: '#78A9FF'}, // 3
    {background: '#FDDC69', highlight: '#D2A106'}, // 4
    {background: '#84F4D3', highlight: '#62B89E'}, // 5
    {background: '#FFD6E8', highlight: '#FF7EB6'}, // 6
    {background: '#9EF0F0', highlight: '#08BDBA'}, // 7
    {background: '#EAE47F', highlight: '#AFAB5E'}, // 8
    {background: '#A7F0BA', highlight: '#42BE65'}, // 9
  ];
  const overrides: Partial<Record<MerieEventTypeEnum, DocumentColorsPartial>> = {};
  const DEFAULT_COLORS = {background: '#E2E8F2', highlight: '#9BA8BD'};
  return (eventType: MerieEventTypeEnum) => {
    const enumValueIndex = eventTypeEnum.findIndex((v) => v.name === eventType);
    return {
      text: 'var(--dlAgGridFontColor)',
      ...(overrides[eventType] ?? COLOR_LIST[enumValueIndex % COLOR_LIST.length] ?? DEFAULT_COLORS),
    };
  };
}
function getMerieSnippetPriorityTable(_eventTypeEnum: enumContainer.EnumDefinition[]) {
  return [];
  // return eventTypeEnum.map((v) => v.name);
}

export const HospitalizationDocument: React.FC<Props> = (props) => {
  const {documentData, documentVersionId, storageIdPrefix, maximized, setMinimizedMaximized} = props;
  const theme = useTheme();
  const styles = getStyles({theme});

  const areExtractedDataOpen = useAppSelector((state) => state[APPLICATION_REDUCER_STATE_ID].areExtractedDataOpen);
  const dispatch = useAppDispatch();

  const handleToggleExtractedData = () => {
    dispatch(applicationSlice.actions.setAreExtractedDataOpen(!areExtractedDataOpen));
  };

  const formatters = useFormatters();
  const {tEnum, formatDateTimeShort} = formatters;
  const documentName = documentData?.documentName ?? '';
  const formattedTimestamp = documentData?.documentTimestamp ? formatDateTimeShort(documentData?.documentTimestamp) : undefined;
  // Per PDW-48/PDW-49: `documentIsMedicationPlaceholder` is the SINGLE source
  // of truth that decides whether to render the structured-medication
  // placeholder table or the regular document content. No empty-content
  // fallback or URL-pattern detection — purely the variable. The field name
  // intentionally differs from the homonymous `isMedicationPlaceholder` used
  // on timeline annotations / docs-list / merge DTOs to avoid name collisions.
  const isMedicationPlaceholder = Boolean(documentData?.documentIsMedicationPlaceholder);
  const placeholderDocumentVersionId = documentVersionId;

  const [defaultScroll, setDefaultScroll] = useLocalStorage(`${storageIdPrefix}.docScroll`, 0);

  const store = useStore();
  const state = store && store.getState();
  const eventTypeEnum = enumContainer.getEnumValues(state, 'MerieEventTypeEnum');
  const getDocumentColors = useMerieDocumentColors(eventTypeEnum) as (type: string) => DocumentColors;
  const [disabledEventTypes, setDisabledEventTypes] = useState({} as Record<string, boolean>);
  const {formattedDocumentSnippets, legendTypes} = useDocumentInfo({
    document: documentData,
    disabledEventTypes: Object.keys(disabledEventTypes).filter((v) => disabledEventTypes[v]),
    formatters,
    getDocumentColors,
    snippetPriorityTable: getMerieSnippetPriorityTable(eventTypeEnum),
  });
  if (isMedicationPlaceholder) {
    return (
      <div css={styles.wrapper}>
        <IconHeaderWithSearch iconName={'medicalFileIcon'} label={documentName} childrenWrapperStyle={{gap: 8}}>
          <MaximizeButton maximized={maximized} toggleMaximized={() => setMinimizedMaximized({maximized: !maximized})} />
        </IconHeaderWithSearch>
        {(formattedTimestamp || documentData?.documentOrganization) && (
          <div css={styles.documentChips}>
            {formattedTimestamp && <DlMuiChip label={formattedTimestamp} size={'medium'} inputCss={styles.chip} />}
            {documentData?.documentOrganization && (
              <DlMuiChip label={documentData?.documentOrganization} size={'medium'} inputCss={styles.chip} />
            )}
          </div>
        )}
        <MedicationPlaceholderDocument documentVersionId={placeholderDocumentVersionId} />
      </div>
    );
  }

  return (
    <div css={styles.wrapper}>
      <IconHeaderWithSearch iconName={'medicalFileIcon'} label={documentName} childrenWrapperStyle={{gap: 8}}>
        <LightIconButton
          data-name={'dl-mui-toggle-extracted-data--icon-button'}
          iconName='tableIcon'
          useFixedViewBox={16}
          style={{width: 16}}
          title={areExtractedDataOpen ? 'document.hide.extractedInfo' : 'document.show.extractedInfo'}
          onClick={handleToggleExtractedData}
          selected={areExtractedDataOpen}
        />
        <MaximizeButton maximized={maximized} toggleMaximized={() => setMinimizedMaximized({maximized: !maximized})} />
      </IconHeaderWithSearch>
      <DlMuiComponentBox
        defaultScroll={defaultScroll}
        onScroll={(event) => {
          setDefaultScroll((event.target as HTMLDivElement).scrollTop);
        }}
      >
        <div css={styles.documentWrapper}>
          <div css={styles.documentContentWrapper}>
            {(formattedTimestamp || documentData?.documentOrganization) && (
              <div css={styles.documentChips}>
                {formattedTimestamp && <DlMuiChip label={formattedTimestamp} size={'medium'} inputCss={styles.chip} />}
                {documentData.documentOrganization && (
                  <DlMuiChip label={documentData?.documentOrganization} size={'medium'} inputCss={styles.chip} />
                )}
              </div>
            )}
            <RenderedDocument formattedDocumentSnippets={formattedDocumentSnippets} isLoading={false} />
          </div>
          <Collapse
            css={styles.conceptsCollapse}
            data-name={'hospitalization-document--collapse'}
            in={areExtractedDataOpen}
            orientation={'horizontal'}
            timeout={500}
          >
            <div css={styles.conceptsWrapper}>
              <DlMuiDocumentConcepts
                tags={legendTypes.map((v) => ({
                  id: v,
                  label: tEnum('MerieEventTypeEnum', v),
                  selected: !disabledEventTypes[v],
                }))}
                onClickTag={(id) => {
                  setDisabledEventTypes({
                    ...disabledEventTypes,
                    [id]: !disabledEventTypes[id],
                  });
                }}
                getDocumentColors={getDocumentColors}
                showBoxHeader
              />
              {/* TODO: removed for now - it will be added when features will be implemented */}
              {/* <DocumentFeedContainer */}
              {/*  currentTextChunks={documentSnippets} */}
              {/*  documentStateId={documentStateId} */}
              {/*  isMui */}
              {/* /> */}
            </div>
          </Collapse>
        </div>
      </DlMuiComponentBox>
    </div>
  );
};

HospitalizationDocument.displayName = 'HospitalizationDocument';

const getStyles: GetStylesFunction = (options) => ({
  wrapper: css`
    background-color: #ffffff;
    gap: ${options?.theme?.spacing(3)};
    display: flex;
    flex-direction: column;
    padding: ${options?.theme?.spacing(4)};
    align-items: flex-start;
    align-self: stretch;
    border-radius: ${options?.theme?.spacing(6)};
    height: 100%;
    overflow: hidden;
  `,
  documentWrapper: css`
    display: flex;
    flex-direction: row;
    gap: ${options?.theme?.spacing(3)};
  `,
  documentContentWrapper: css`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
  `,
  documentChips: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: ${options?.theme?.spacing(3)};
  `,
  chip: css({
    'backgroundColor': `${options?.theme?.palette.custom.coolGray['50']}`,
    'marginRight': `${options?.theme?.spacing(3)}`,
    'height': '24px',
    '& .MuiChip-label': {
      paddingLeft: `${options?.theme?.spacing(1)}`,
      paddingRight: `${options?.theme?.spacing(1)}`,
      fontSize: 'var(--dlFontSizeNormal)',
      color: `${options?.theme?.palette.custom.coolGray['900']}`,
    },
  }),
  conceptsWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${options?.theme?.spacing(3)};
    width: 240px;
  `,
  conceptsCollapse: css`
    flex: 0 0 auto;
  `,
});
